// Build Line - the orders board.
//
// The screen an operator leaves open all day, so it is built around one
// question: WHAT IS WAITING ON A HUMAN. That is the attention strip at the top -
// every parked item across every live order, with the typed code that parked it
// and one click through to the order it belongs to. The table underneath is the
// rest of the picture: where each order is, how much of the hard-gate checklist
// it has cleared, and how many items it has stopped on.
//
// POLLING (10 s, the list cadence the rest of the app uses; the detail polls at
// 3 s) IS PAUSED WHILE THE TAB IS HIDDEN. Two reads per lap, one of them a full
// aggregate over every order's items, is not a query to leave running against
// production for the eight hours a background tab sits there. Coming back to the
// tab refreshes immediately, so a paused board is never a stale one on screen.
//
// SECURITY: nothing here can render a borrowed credential - the list endpoint
// carries `has_*` booleans and `secrets_purged_at`, never a key.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { BuildShowToast } from './BuildLine';
import { AttentionMeaning } from './DeliveryMeaning';
import { groupAttention } from './presentation';
import {
  buildApi,
  buildErrorMessage,
  buildIsParked,
  type BuildAttentionRow,
  type BuildOrderListRow,
  type BuildOrderFilters,
  type BuildOrderStatus,
  type BuildProfile,
} from './buildApi';

/** Same 10 s as every other list screen in the app. */
const POLL_MS = 10_000;

/** Statuses nothing but an operator will move. Both are honey in the table and
 *  both put their items on the strip above it. */
const PARKED_STATUSES: ReadonlySet<BuildOrderStatus> = new Set<BuildOrderStatus>([
  'needs_attention',
  'awaiting_capacity',
]);

export const BUILD_STATUS_LABEL: Record<BuildOrderStatus, string> = {
  draft: 'draft',
  preflight: 'preflight',
  dry_run_complete: 'dry run done',
  running: 'running',
  needs_attention: 'needs attention',
  awaiting_capacity: 'awaiting capacity',
  delivered: 'delivered',
  closed: 'closed',
  aborted: 'aborted',
};

/** The status colour language from BuildLine.css: sage for done, blue for in
 *  flight, honey for waiting on a human or on capacity, rose for refused, muted
 *  for not started. Closed is completed successfully. */
export function buildStatusTone(status: BuildOrderStatus): 'ok' | 'info' | 'warn' | 'err' | 'muted' {
  switch (status) {
    case 'delivered':
    case 'closed':
      return 'ok';
    case 'running':
    case 'preflight':
      return 'info';
    case 'needs_attention':
    case 'awaiting_capacity':
      return 'warn';
    case 'aborted':
      return 'err';
    default:
      // draft, dry_run_complete: not building yet.
      return 'muted';
  }
}

export function buildPhaseTone(phase: string, status: BuildOrderStatus): 'ok' | 'muted' {
  return phase === 'deliver' && (status === 'delivered' || status === 'closed') ? 'ok' : 'muted';
}

/** Compact age. An order that has been running for four hours is a different
 *  animal from one that started four minutes ago, and an absolute timestamp
 *  makes the reader do that subtraction themselves. */
export function ago(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d`;
}

export function fmtDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      })
    : '-';
}

/**
 * Poll `tick` every `ms`, but only while the document is visible.
 *
 * The px3 tab polls unconditionally; this is the one deliberate departure, and
 * it is cheap. A hidden tab's laps buy nobody anything - nothing is being read -
 * while each one costs a full aggregate over every order's items. Becoming
 * visible fires an immediate catch-up read BEFORE restarting the interval, so
 * the operator never looks at a board that is one whole period out of date.
 *
 * `tick` must be a stable callback (useCallback): every identity change tears
 * the interval down and builds a new one, which on an unstable function would
 * mean an interval that resets on every render and therefore never fires.
 */
export function useVisiblePoll(tick: () => void, ms: number, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    let timer: number | null = null;
    const stop = (): void => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const start = (): void => {
      if (timer === null) timer = window.setInterval(tick, ms);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        tick();
        start();
      } else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tick, ms, enabled]);
}

/** A ratio and the bar that draws it. Zero of zero is not 100 percent: an order
 *  whose items have not been written yet has made no progress, and a full bar
 *  would say the opposite. */
export function Progress({ done, total }: { done: number; total: number }): JSX.Element {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const complete = total > 0 && done >= total;
  return (
    <div className="bl-progress" title={`${done} of ${total}`}>
      <div className="bl-progress-track">
        <div
          className={`bl-progress-fill ${complete ? 'bl-progress-fill--done' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="bl-num">
        {done}/{total}
      </span>
    </div>
  );
}

export function Orders({
  onOpen,
  showToast,
  filters,
  onFiltersChange,
}: {
  onOpen: (orderId: number) => void;
  showToast: BuildShowToast;
  filters: BuildOrderFilters;
  onFiltersChange: (filters: BuildOrderFilters) => void;
}): JSX.Element {
  const [orders, setOrders] = useState<BuildOrderListRow[]>([]);
  const [attention, setAttention] = useState<BuildAttentionRow[]>([]);
  const [profiles, setProfiles] = useState<BuildProfile[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState(filters.search.trim());
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(filters.search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  /** Monotonic request id: a slow lap must never overwrite a newer one, and the
   *  read fired by a visibility change lands beside an in-flight interval read
   *  often enough for that to be a real race rather than a theoretical one. */
  const reqId = useRef(0);

  /**
   * A failed load is NOT an empty board. "No orders yet" reads as "nothing is
   * running", which is the wrong conclusion to draw about a pipeline that might
   * have three parked builds in it - so the failure is state that persists until
   * a read succeeds, and the last good rows stay on screen underneath it.
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    const mine = ++reqId.current;
    setRefreshing(true);
    try {
      const [freshOrders, freshAttention] = await Promise.all([
        buildApi.listOrders({ search, status: filters.status, exclude_aborted: filters.exclude_aborted }),
        buildApi.listAttention(),
      ]);
      if (mine !== reqId.current) return false;
      setOrders(freshOrders);
      setAttention(freshAttention);
      setLoadError(null);
      return true;
    } catch (err) {
      if (mine !== reqId.current) return false;
      setLoadError(buildErrorMessage(err));
      return false;
    } finally {
      if (mine === reqId.current) {
        setLoaded(true);
        setRefreshing(false);
      }
    }
  }, [search, filters.status, filters.exclude_aborted]);

  useEffect(() => {
    void refresh();
    return () => { reqId.current += 1; };
  }, [refresh]);

  /** Profiles are read ONCE. They are the routing cards an order was stamped
   *  from - they do not change while a build runs - and re-reading them ten
   *  times a minute for one name column would be a query for nothing. */
  useEffect(() => {
    let live = true;
    void buildApi
      .listProfiles()
      .then((rows) => {
        if (live) setProfiles(rows);
      })
      .catch(() => {
        // The name is decoration; the order carries its profile id either way.
      });
    return () => {
      live = false;
    };
  }, []);

  const tick = useCallback(() => {
    void refresh();
  }, [refresh]);
  useVisiblePoll(tick, POLL_MS, true);

  const profileName = (id: number): string =>
    profiles.find((p) => p.id === id)?.name ?? `profile ${id}`;

  // Soft codes are not parks: a pending tracking certificate is a note for the
  // finisher sweep, not work waiting on a human, and a strip that lists them is
  // one nobody reads. Counted, not listed.
  const parked = attention.filter((row) => buildIsParked(row.error_code));
  // One issue per order, item kind and cause; every affected item stays inspectable.
  const grouped = groupAttention(parked);

  return (
    <>
      {loadError !== null && (
        <div className="bl-notice bl-notice--err bl-notice--standalone">
          <strong>The board could not be refreshed.</strong> {loadError}. What is below is the last
          good read, not an empty pipeline.
        </div>
      )}

      <section className="bl-card">
        <div className="bl-card-header">
          <h2 className="bl-card-title">
            Waiting on you
            {parked.length > 0 && (
              <span className="bl-badge bl-badge--warn bl-badge--inline">
                {parked.length} item{parked.length === 1 ? '' : 's'}
              </span>
            )}
          </h2>
          <div className="bl-actions">
            <button
              type="button"
              className="bl-btn bl-btn--ghost bl-btn--small"
              onClick={() => {
                void refresh().then((okRead) => {
                  if (okRead) showToast('info', 'Board refreshed.');
                });
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        {parked.length === 0 ? (
          <div className="bl-card-body">
            <div className="bl-notice bl-notice--ok">
              <strong>No blocked items reported.</strong> Open an order for its progress and delivery checks.
            </div>
          </div>
        ) : (
          <>
            <div className="bl-table-wrap bl-table-wrap--short">
              <table className="bl-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th className="bl-col-center">Kind</th>
                    <th>Affected items</th>
                    <th>Issue, impact and next step</th>
                    <th className="bl-col-center">Recorded</th>
                    <th className="bl-col-center" />
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(({ key, row, items }) => (
                    <tr key={key}>
                      <td>
                        {row.order_label}{' '}
                        <span className="bl-cell-faint">#{row.order_id}</span>
                      </td>
                      <td className="bl-col-center bl-cell-faint">{row.kind}</td>
                      <td>
                        <span>{items.length} affected {items.length === 1 ? 'item' : 'items'}</span>
                        <details className="bl-details"><summary>View affected items</summary>
                          <div className="bl-details-body bl-mono">{items.map((item) => <div key={item}>{item}</div>)}</div>
                        </details>
                      </td>
                      <td><AttentionMeaning explanation={row.explanation} code={row.error_code} /></td>
                      <td className="bl-col-center bl-cell-faint" title={fmtDate(row.error_at)}>
                        {ago(row.error_at)}
                      </td>
                      <td className="bl-col-center">
                        <button
                          type="button"
                          className="bl-btn bl-btn--secondary bl-btn--small"
                          onClick={() => onOpen(row.order_id)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="bl-card">
        <div className="bl-card-header">
          <h2 className="bl-card-title">Orders</h2>
          <span className="bl-hint">
            Track each order and open it for verified results or the next action needed.
          </span>
        </div>
        <div className="bl-order-filters">
          <label className="bl-order-search">
            <span className="bl-field-label">Search orders</span>
            <input type="search" className="bl-input" placeholder="Order name, ID, profile or customer"
              value={filters.search} maxLength={200}
              onChange={(event) => onFiltersChange({ ...filters, search: event.target.value })} />
          </label>
          <label>
            <span className="bl-field-label">Status</span>
            <select className="bl-input" value={filters.status}
              onChange={(event) => onFiltersChange({ ...filters,
                status: event.target.value as BuildOrderFilters['status'],
                exclude_aborted: event.target.value === 'aborted' ? false : filters.exclude_aborted,
              })}>
              <option value="all">All orders</option>
              <option value="active">Active</option>
              {Object.entries(BUILD_STATUS_LABEL).map(([status, label]) => (
                <option key={status} value={status}>{label[0].toUpperCase() + label.slice(1)}</option>
              ))}
            </select>
          </label>
          <label className="bl-order-hide">
            <input type="checkbox" checked={filters.exclude_aborted}
              onChange={(event) => onFiltersChange({ ...filters, exclude_aborted: event.target.checked,
                status: event.target.checked && filters.status === 'aborted' ? 'all' : filters.status,
              })} />
            Hide aborted
          </label>
          <button type="button" className="bl-btn bl-btn--ghost bl-btn--small"
            onClick={() => onFiltersChange({ search: '', status: 'all', exclude_aborted: false })}>
            Clear filters
          </button>
          <span className="bl-order-result bl-hint" role="status">
            {refreshing || filters.search.trim() !== search ? 'Updating orders…'
              : `${orders.length}${orders.length === 100 ? '+' : ''} matching order${orders.length === 1 ? '' : 's'}`}
            {orders.length === 100 && ' · Showing the newest 100 matches. Refine your search to see older orders.'}
          </span>
        </div>
        <div className="bl-table-wrap" aria-busy={refreshing}>
          <table className="bl-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Profile</th>
                <th className="bl-col-center">Phase</th>
                <th className="bl-col-center">Status</th>
                <th>Build checklist</th>
                <th className="bl-col-center">Domains</th>
                <th className="bl-col-center">Mailboxes</th>
                <th className="bl-col-center">Parked</th>
                <th className="bl-col-center">Created</th>
                <th className="bl-col-center" />
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="bl-empty">
                    {!loaded
                      ? 'Loading orders...'
                      : loadError !== null
                        ? 'The order list could not be loaded - this is not an empty list. Use Refresh to try again.'
                        : 'No orders match these filters. Clear filters to see all orders, or enroll a new order.'}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  // No row-level onClick: a clickable <tr> is mouse-only unless
                  // it grows tabIndex, a role and a key handler, and the row
                  // already carries a real Open button a keyboard reaches.
                  <tr key={order.id} className={order.parked_items > 0 ? 'bl-row--bad' : ''}>
                    <td>
                      {order.label} <span className="bl-cell-faint">#{order.id}</span>
                    </td>
                    <td className="bl-cell-faint">{profileName(order.profile_id)}</td>
                    <td className="bl-col-center">
                      <span className={`bl-badge bl-badge--${buildPhaseTone(order.phase, order.status)}`}>{order.phase}</span>
                    </td>
                    <td className="bl-col-center">
                      <span className={`bl-badge bl-badge--${buildStatusTone(order.status)}`}>
                        {BUILD_STATUS_LABEL[order.status]}
                      </span>
                    </td>
                    <td>
                      <Progress done={order.hard_gate.done} total={order.hard_gate.total} />
                    </td>
                    <td className="bl-col-center bl-num" title="DKIM active / total">
                      {order.domains_dkim_active}/{order.domains_total}
                    </td>
                    <td className="bl-col-center bl-num" title="renamed / total">
                      {order.mailboxes_renamed}/{order.mailboxes_total}
                    </td>
                    <td className="bl-col-center">
                      {order.parked_items > 0 ? (
                        <span className="bl-badge bl-badge--err">{order.parked_items}</span>
                      ) : PARKED_STATUSES.has(order.status) ? (
                        // The status says a human is needed but no item carries
                        // a live error: that is the capacity tick waiting for
                        // seats, not an item failure. Saying "0" would read as
                        // "nothing wrong" on an order that is not moving.
                        <span className="bl-badge bl-badge--warn">order</span>
                      ) : (
                        <span className="bl-cell-faint">-</span>
                      )}
                    </td>
                    <td className="bl-col-center bl-cell-faint" title={fmtDate(order.created_at)}>
                      <time className="bl-order-date" dateTime={order.created_at}>
                        <span>{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span>{new Date(order.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}</span>
                      </time>
                    </td>
                    <td className="bl-col-center">
                      <div className="bl-rowactions">
                        <button
                          type="button"
                          className="bl-btn bl-btn--secondary bl-btn--small"
                          onClick={() => onOpen(order.id)}
                        >
                          Open
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
